import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { when } from "lit/directives/when.js";

import type { InputSize, InputWidth } from "@/components/form/atlas-input/types";
import FormControl from "@/components/form/form-control";
import { Watch } from "@/decorators/watch";
import { emit } from "@/internals/events";
import { WithCharsCounterMixin } from "@/internals/mixins/with-chars-counter-mixin";
import EmojiValidator from "@/internals/validators/emoji-validator";

import textAreaStyle from "./atlas-textarea.scss";

/**
 * @event {CustomEvent} atlas-textarea-focus - Evento lançado quando é dado foco no textarea
 * @event {CustomEvent} atlas-textarea-blur - Evento lançado quando é retirado o foco do textarea
 * @event {CustomEvent} atlas-textarea-change - Evento lançado quando o valor do textarea é alterado
 *
 * @tag atlas-textarea
 */
@customElement("atlas-textarea")
export default class AtlasTextarea extends WithCharsCounterMixin(FormControl) {
    static styles = textAreaStyle;

    /** O tamanho do textarea */
    @property({ type: String }) size: InputSize = "md";

    /** A largura do textarea */
    @property({ type: String }) width: InputWidth = "auto";

    /** A mensagem que aparecerá quando o textarea está vazio */
    @property({ type: String }) placeholder = "";

    /** A altura visível do textarea (em linhas) */
    @property({ type: Number }) rows = 4;

    /** Indica que a altura visível do textarea pode ser redimensionada */
    @property({ type: Boolean }) resize = false;

    /** Indica que o textarea pode conter emoji */
    @property({ type: Boolean, attribute: "enable-emoji" }) enableEmoji = false;

    @query("textarea") textareaRef: HTMLTextAreaElement;

    /** @internal */
    public connectedCallback(): void {
        super.connectedCallback?.();

        this.updateComplete.then(() => {
            if (!this.enableEmoji) {
                this.addValidator(new EmojiValidator());
            }
        });
    }

    /** @internal */
    @Watch("value")
    public onChangeValue() {
        emit(this, "atlas-textarea-change", { detail: this.value, trackDisable: true });
    }

    /**
     * Define o foco no textarea
     */
    public focus() {
        this.textareaRef.focus();
    }

    /**
     * Remove o foco do textarea
     */
    public blur() {
        this.textareaRef.blur();
    }

    private handleFocus() {
        emit(this, "atlas-textarea-focus", { trackDisable: true });
    }

    private handleBlur() {
        this.reportValidity();

        emit(this, "atlas-textarea-blur", { trackDisable: true });
        emit(this, "atlas-form-element-touch", { trackDisable: true });
    }

    private handleInput() {
        this.value = this.textareaRef.value;
    }

    private renderFeedbackGroup() {
        const feedbackGroupClass = {
            "input-feedback-group": true,
            "is-skeleton-loading": this.skeletonLoading,
            "has-helper-text": this.hasHelperText(),
            "has-chars-counter": this.hasCharsCounter(),
            "has-default-chars-counter": this.hasDefaultCharsCounter(),
            "has-status-feedback": this.getShowStatus() && this._statusMessage !== ""
        };

        return html`
            <div class=${classMap(feedbackGroupClass)}>
                ${this.renderStatusMessage()} ${this.renderCharsCounter()}
                ${this.renderHelperText({ isInputHelper: true })}
            </div>
        `;
    }

    private renderTextarea() {
        const ariaLabel = this.disabled || this.readonly ? this.placeholder : "";
        const ariaDescribedBy = this.helperText ? `${this.id}-helper` : null;

        const inputClass = {
            "form-control": true,
            [`form-control-${this.size}`]: !!this.size,
            "resizable": this.resize,
            "readonly": this.readonly,
            "disabled": this.disabled,
            [`is-${this._status}`]: this.getShowStatus()
        };

        return html`
            <div class="input-group">
                <textarea
                    aria-describedby="${ifDefined(ariaDescribedBy)}"
                    aria-label="${ariaLabel}"
                    id="${this.id}"
                    name="${this.name}"
                    class=${classMap(inputClass)}
                    placeholder="${this.placeholder}"
                    rows=${ifDefined(this.rows || undefined)}
                    .value=${live(this.value)}
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                    ?required=${this.required}
                    @focus=${this.handleFocus}
                    @blur=${this.handleBlur}
                    @input=${this.handleInput}
                    autocomplete="off"
                >
                </textarea>
            </div>
        `;
    }

    /** @override */
    protected renderElement() {
        return html` ${this.renderLabel()} ${this.renderTextarea()} ${this.renderFeedbackGroup()} `;
    }

    /** @override */
    protected renderSkeleton() {
        const inputClass = {
            "skeleton": true,
            "form-control": true,
            "form-control-sm": this.size === "sm",
            "form-control-lg": this.size === "lg"
        };

        return html`
            ${when(!!this.label, () => html`<div class="form-label skeleton"></div>`)}
            <div class="input-group">
                <div class="${classMap(inputClass)}"></div>
            </div>
            ${this.renderFeedbackGroup()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-textarea": AtlasTextarea;
    }
}
